import React, {useEffect, useRef} from 'react';
import {EVENT_TYPE_CALL, EVENT_TYPE_CHAT, EVENT_TYPE_EMAIL, EVENT_TYPE_FEEDBACK} from 'utils/eventConstants';
import useData from '@/utils/useData';
import {EventDetails} from '@/types/event';
import {buildEventURL} from '@/utils/urlBuilder';
import EventDetailBlockWithPlayer from './EventDetailBlockWithPlayer';
import EventDetailBlockWithoutPlayer from './EventDetailBlockWithoutPlayer';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import CellLoader from '@/components/loading/CellLoader';
import i18n from 'i18n';
import {useAlert} from '@/components/alerts';
import {useSafeNavigation} from '@/hooks/useSafeNavigation';
import axios from 'axios';

interface EventDetailBlockProps {
    activeEventId: number;
}

const EventDetailBlock: React.FC<EventDetailBlockProps> = React.memo(
    ({activeEventId}) => {
        const {config, loading: configLoading} = useOptimizedConfig();
        const {showError} = useAlert();
        const {navigateToRoot} = useSafeNavigation();
        const errorHandledRef = useRef<boolean>(false);
        
        const [eventDetails, loading, fetchData, error] = useData<EventDetails>(
            config ? buildEventURL('eventDetails', activeEventId) : null,
            'eventDetail',
            'setEventDetails',
            'eventDetails',
        );

        useEffect(() => {
            if (error && axios.isAxiosError(error) && error.response?.status === 403 && !errorHandledRef.current) {
                errorHandledRef.current = true;
                const errorMessage = error.response?.data?.message || 
                    `Event with id ${activeEventId} does not exist or you do not have permission to view it`;
                showError(errorMessage);
                navigateToRoot({replace: true});
            }
        }, [error, activeEventId, showError, navigateToRoot]);

        useEffect(() => {
            errorHandledRef.current = false;
        }, [activeEventId]);

        useEffect(() => {
            if (config && window.URLS?.eventDetails && !errorHandledRef.current) {
                fetchData({});
            }
        }, [activeEventId, fetchData, config]);

        if (error && axios.isAxiosError(error) && error.response?.status === 403) {
            return null;
        }

        if (configLoading || !config) {
            return <CellLoader text={i18n.t('loading_configuration.text')} fullHeight={true} />;
        }

        let textContents: React.ReactNode;
        
        if (eventDetails?.eventTypeName === EVENT_TYPE_CALL) {
            textContents = <EventDetailBlockWithPlayer activeEventId={activeEventId} eventDetails={eventDetails}/>;
        } else if (eventDetails?.eventTypeName === EVENT_TYPE_CHAT || eventDetails?.eventTypeName === EVENT_TYPE_EMAIL || eventDetails?.eventTypeName === EVENT_TYPE_FEEDBACK) {
            textContents = <EventDetailBlockWithoutPlayer activeEventId={activeEventId} eventDetails={eventDetails} loading={loading}/>;
        }

        return <div>{textContents}</div>;
    }
);

export default EventDetailBlock; 